import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { HomeworkActivityHttpModel, HomeworkActivityQuery, HomeworkActivityStore } from '@examdojo/homework';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { QuestionAttemptEntityService } from '@examdojo/question';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { UserQuery } from '@examdojo/user';
import { concatMap, filter, from, map, of, switchMap, withLatestFrom } from 'rxjs';
import { ExamdojoPlatformService } from '../shared/platform.service';
import { HomeworkActivityDialogService } from '../features/homework/dialog/homework-activity-dialog.service';

const TERMINAL_STATUSES: Array<HomeworkActivityHttpModel['status']> = ['COMPLETED', 'DISCARDED', 'FAILED', 'PAUSED'];

@Injectable()
export class HomeworkActivityRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly userQuery: UserQuery,
    private readonly store: HomeworkActivityStore,
    private readonly query: HomeworkActivityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly homeworkActivityDialogService: HomeworkActivityDialogService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {
    if (this.featureFlagService.get(ExamDojoFeatureFlag.Classes)) {
      this.listenForHomeworkActivityStatusChange().pipe(takeUntilDestroyed()).subscribe();
    }
  }

  private listenForHomeworkActivityStatusChange() {
    return this.userQuery.active$.pipe(
      filter(Boolean),
      switchMap((user) =>
        this.realtimeService.listenBroadcastRealtime<HomeworkActivityHttpModel>(
          [{ event: RealtimeChangesListenEvent.INSERT }, { event: RealtimeChangesListenEvent.UPDATE }],
          `homework_activity_status:${user.id}`,
        ),
      ),
      withLatestFrom(this.platformService.activeStatusState$),
      filter(([_, isActive]) => isActive),
      map(([event]) => event),
      concatMap((payload) => {
        const record = payload?.payload?.record;

        if (!record?.id) {
          return of(null);
        }

        const storeActivity = this.query.getEntity(record.id);

        if (storeActivity && new Date(storeActivity.updated_at) >= new Date(record.updated_at)) {
          return of(null);
        }

        if (storeActivity && TERMINAL_STATUSES.includes(record.status) && storeActivity.status !== record.status) {
          return this.handleTerminalHomeworkActivityStatusChange(record);
        }

        if (
          !storeActivity ||
          storeActivity.question_attempt_id !== record.question_attempt_id ||
          storeActivity.status !== record.status ||
          storeActivity.updated_at !== record.updated_at
        ) {
          return this.handleInProgressHomeworkActivityStatusChange(record);
        }

        return of(null);
      }),
    );
  }

  private handleInProgressHomeworkActivityStatusChange(record: HomeworkActivityHttpModel) {
    this.store.upsert(record.id, record);
    return of(null);
  }

  private handleTerminalHomeworkActivityStatusChange(record: HomeworkActivityHttpModel) {
    this.questionAttemptService.resetStore();
    this.store.update(record.id, record);

    if (this.homeworkActivityDialogService.isOpen()) {
      return from(this.homeworkActivityDialogService.dismiss()).pipe(map(() => null));
    }

    return of(null);
  }
}
